import {
  Component, Input, signal, computed,
  ChangeDetectionStrategy, ViewEncapsulation,
  Directive, HostListener, inject, contentChild, effect,
  ElementRef, OnInit, OnDestroy, afterNextRender,
} from '@angular/core';
import {NgControl, Validators} from '@angular/forms';
import {CommonModule} from '@angular/common';

// ── MmInput directive — attach to <input> / <textarea> inside mm-field ──────
@Directive({
  selector: 'input[mmInput], textarea[mmInput]',
  standalone: true,
  host: {class: 'mm-input'},
})
export class MmInputDirective {}

// ── MmSelect directive — attach to <select> inside mm-field ─────────────────
@Directive({
  selector: 'select[mmSelect]',
  standalone: true,
  host: {class: 'mm-select'},
})
export class MmSelectDirective {}

/** Makes each field's ids unique on the page. */
let nextFieldId = 0;

/** What may take a field's name when it is not an mmInput / mmSelect (a product picker, a slider). */
const OTHER_CONTROLS = 'input, select, textarea, button, [role="combobox"], [role="slider"], [role="textbox"]';

// ── MmField wrapper component ────────────────────────────────────────────────
@Component({
  selector: 'mm-field',
  standalone: true,
  imports: [CommonModule],
  // Eager like the other 109 components. This wrapper carries the label,
  // hint and error for every form field in the app, and under OnPush any
  // of those changing without a new object reference simply did not
  // repaint. One component disagreeing is also what makes the rule
  // impossible to enforce anywhere else.
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
  template: `
    <div class="mmf-wrap" [class.mmf-focused]="focused()" [class.mmf-error]="hasError()"
      [class.mmf-required]="isRequired()" [class.mmf-required-empty]="isRequiredEmpty()"
      [class.mmf-disabled]="disabled()">
      @if (label) {
        <label class="mmf-label" [attr.id]="labelId" [attr.for]="controlId()">
          {{ label }}@if (isRequired()) {<span class="mmf-required-mark" aria-hidden="true">*</span>}
        </label>
      }
      <div class="mmf-control">
        @if (prefixIcon) {
          <span class="mmf-prefix"><i class="material-icons">{{ prefixIcon }}</i></span>
        }
        <ng-content></ng-content>
        @if (suffix) {
          <span class="mmf-suffix-text">{{ suffix }}</span>
        }
        @if (suffixIcon) {
          <span class="mmf-suffix"><i class="material-icons">{{ suffixIcon }}</i></span>
        }
      </div>
      @if (error) {
        <span class="mmf-error-msg" [attr.id]="messageElementId" role="alert">{{ error }}</span>
      } @else if (hint) {
        <span class="mmf-hint" [attr.id]="messageElementId">{{ hint }}</span>
      }
    </div>
  `,
})
export class MmFieldComponent {
  @Input() label  = '';
  @Input() hint   = '';
  @Input() suffix = '';
  @Input() prefixIcon = '';
  @Input() suffixIcon = '';
  @Input() error  = '';   // static error string override

  focused  = signal(false);
  disabled = signal(false);

  /**
   * Ties the label, the control and the hint or error together for assistive
   * technology. The label used to sit beside the projected control with no
   * `for`, so it named nothing: a screen reader announced "edit text,
   * required" with no word of which field, clicking the label did not focus
   * it, and an error appearing under it was never read out.
   */
  private readonly _fieldId = `mmf-${++nextFieldId}`;
  readonly labelId = `${this._fieldId}-label`;
  readonly messageElementId = `${this._fieldId}-msg`;
  /** The id of the mmInput / mmSelect the label is `for` (the first one, when there are several). */
  readonly controlId = signal<string | null>(null);
  private _controlCount = 0;
  private _host = inject(ElementRef<HTMLElement>);

  // NgControl projected into the field (set when the input carries
  // formControl/formControlName/ngModel). Drives the .mmf-error ring.
  private _ngControl = contentChild(NgControl);
  // Bumped on the control's status changes / blur so the computed below
  // re-evaluates (control validity flags are not signals).
  private _statusTick = signal(0);

  constructor() {
    effect((onCleanup) => {
      const ctrl = this._ngControl()?.control;
      if (!ctrl) return;
      const sub = ctrl.statusChanges.subscribe(() => this._statusTick.update(v => v + 1));
      onCleanup(() => sub.unsubscribe());
    });
    afterNextRender(() => this.nameOtherControl());
  }

  /** An id for a projected mmInput / mmSelect that has none of its own. */
  newControlId(): string {
    return `${this._fieldId}-control-${++this._controlCount}`;
  }

  /** Called by MmFocusDirective: the first control becomes the label's target. */
  registerControl(id: string): boolean {
    if (!this.controlId()) {
      this.controlId.set(id);
    }
    return this.controlId() === id;
  }

  unregisterControl(id: string) {
    if (id && this.controlId() === id) {
      this.controlId.set(null);
    }
  }

  /** The hint or error under the field, which the control is described by; null when neither shows. */
  messageId(): string | null {
    return this.error || this.hint ? this.messageElementId : null;
  }

  /**
   * A field around something other than an mmInput / mmSelect -- the product
   * picker (a button with role combobox, which takes no name from its
   * content), a slider -- gets its name from the label by reference.
   */
  private nameOtherControl() {
    if (this.controlId() || !this.label) {
      return;
    }
    const control = (this._host.nativeElement as HTMLElement)
      .querySelector('.mmf-control')?.querySelector(OTHER_CONTROLS);
    if (control && !control.hasAttribute('aria-label') && !control.hasAttribute('aria-labelledby')) {
      control.setAttribute('aria-labelledby', this.labelId);
    }
  }

  // Red border ring: shown for a static error override, or when the
  // projected control is invalid after the user has interacted with it.
  // Message text itself is owned by each consumer (external mmf-error-msg span).
  hasError = computed(() => {
    this._statusTick();
    if (this.error) return true;
    const ctrl = this._ngControl()?.control;
    return !!(ctrl && ctrl.invalid && (ctrl.dirty || ctrl.touched));
  });

  /**
   * True when the projected control carries Validators.required. Drives the
   * `*` on the label and the required styling, so every required field in the
   * app is marked the same way without each screen hand-rolling it.
   */
  isRequired = computed(() => {
    this._statusTick();
    const ctrl = this._ngControl()?.control;
    return !!ctrl?.hasValidator?.(Validators.required);
  });

  /**
   * Required but still empty. Kept separate from `hasError()` — which only
   * fires once the user has interacted — so a form can show what is
   * outstanding without accusing the user of a mistake up front.
   */
  isRequiredEmpty = computed(() => {
    this._statusTick();
    const ctrl = this._ngControl()?.control;
    if (!ctrl || !this.isRequired()) {
      return false;
    }
    const value = ctrl.value;
    return value === null || value === undefined || String(value).trim().length === 0;
  });

  // Called by MmFocusDirective on projected inputs
  onFocus()  { this.focused.set(true); }
  onBlur()   { this.focused.set(false); this._statusTick.update(v => v + 1); }
}

// ── MmFocus directive — bridges focus/blur events from input to mm-field ─────
// Also links the control to the field's label (by id) and to its hint or
// error (aria-describedby), and reports the error ring as aria-invalid.
@Directive({
  selector: 'input[mmInput], textarea[mmInput], select[mmSelect]',
  standalone: true,
  host: {
    '[attr.aria-describedby]': 'describedBy()',
    '[attr.aria-invalid]': 'invalid()',
    '[attr.aria-labelledby]': 'labelledBy()',
  },
})
export class MmFocusDirective implements OnInit, OnDestroy {
  private _field = inject(MmFieldComponent, {optional: true, skipSelf: false, host: true});
  private _el = inject(ElementRef<HTMLElement>);
  /** The control the field's label is `for`; any further one is named by reference. */
  private _primary = false;
  /** What the template itself wrote, kept rather than replaced by the bindings above. */
  private _ownDescribedBy: string | null = null;
  private _ownLabelledBy: string | null = null;
  private _ownInvalid: string | null = null;

  ngOnInit() {
    const el = this._el.nativeElement as HTMLElement;
    this._ownDescribedBy = el.getAttribute('aria-describedby');
    this._ownLabelledBy = el.getAttribute('aria-labelledby');
    this._ownInvalid = el.getAttribute('aria-invalid');
    if (!this._field) {
      return;
    }
    if (!el.id) {
      el.id = this._field.newControlId();
    }
    this._primary = this._field.registerControl(el.id);
  }

  ngOnDestroy() {
    this._field?.unregisterControl((this._el.nativeElement as HTMLElement).id);
  }

  describedBy(): string | null {
    const ids = [this._ownDescribedBy, this._field?.messageId()].filter(id => !!id);
    return ids.length ? ids.join(' ') : null;
  }

  invalid(): string | null {
    return this._field?.hasError() ? 'true' : this._ownInvalid;
  }

  labelledBy(): string | null {
    if (this._ownLabelledBy) {
      return this._ownLabelledBy;
    }
    if (!this._field || this._primary || !this._field.label
      || (this._el.nativeElement as HTMLElement).hasAttribute('aria-label')) {
      return null;
    }
    return this._field.labelId;
  }

  @HostListener('focus')  onFocus() { this._field?.onFocus(); }
  @HostListener('blur')   onBlur()  { this._field?.onBlur();  }
}

